/**
 * # MenuList
 *
 * This is a menu list item that has expanding abilities to show children.
 *
 * ## Props
 *
 * - `id: string` - The unique identifier for the menu list item.
 * - `text?: string` - The text content of the menu list item.
 * - `children: React.ReactNode` - The content to be displayed when the menu list item is expanded.
 *
 * @see [Design ticket]{@link https://github.com/albert-io/project-management/issues/2780}
 *
 * ## Usage
 *
 * ```tsx
 * import React from 'react';
 * import MenuList from './MenuList';
 *
 * const Example = () => {
 *   return (
 *     <MenuList id="menu-item-1" text="Menu Item 1">
 *       <div>Content for Menu Item 1</div>
 *     </MenuList>
 *   );
 * };
 * ```
 */

import React from 'react';

import WithToggle from '../../helpers/WithToggle/WithToggle.react';
import Expandable from '../../helpers/Expandable/Expandable.react';
import MenuListItem from '../../atoms/MenuListItem/MenuListItem.react';

interface MenuListProps {
  id: string;
  text?: string;
  children: React.ReactNode;
}

type Props<C extends React.ElementType> = PolymorphicComponentProps<C, MenuListProps>;

export const MenuList = <C extends React.ElementType>({
  as,
  id,
  text,
  children,
  ...rest
}: Props<C>) => {
  const WrapperElement = as ?? 'div';
  const buttonId = `expandable-button-${id}`;
  const expandableId = `expandable-area-${id}`;
  const iconButtonProps = {
    id: buttonId,
    'aria-controls': expandableId
  };
  return (
    <WrapperElement>
      <WithToggle dismissOnBlur={false}>
        {({onClick, on}) => (
          <div>
            <MenuListItem
              id={buttonId}
              text={text}
              onIconButtonClick={onClick}
              iconButtonProps={iconButtonProps}
              icon={on ? 'chevron-down' : 'chevron-right'}
              {...rest}
            />
            <Expandable aria-labelledby={buttonId} expanded={on} id={expandableId}>
              {children}
            </Expandable>
          </div>
        )}
      </WithToggle>
    </WrapperElement>
  );
};

export default MenuList;
